import React from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	RadioGroup,
	FormControlLabel,
	Radio,
	Alert,
	Stack
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';

const AlertDeleteConfirmationDialog = ({
	open,
	onClose,
	onConfirm,
	alertData,
	isLoading = false
}) => {
	const [deleteOption, setDeleteOption] = React.useState('self');
	const isShared = alertData?.shareStatus === 'shared' || alertData?.shareInfo?.hasSharedToOthers;
	const isReceived = alertData?.shareStatus === 'received' || alertData?.shareInfo?.isShared;

	const handleConfirm = () => {
		onConfirm(deleteOption === 'all');
	};

	const handleClose = () => {
		setDeleteOption('self');
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle sx={{ 
				display: 'flex', 
				alignItems: 'center', 
				gap: 1,
				color: 'error.main',
				borderBottom: '1px solid #e0e0e0'
			}}>
				<WarningIcon color="error" />
				Delete Alert
			</DialogTitle>
			
			<DialogContent sx={{ pt: 2 }}>
				<Stack spacing={2}>
					<Alert severity="warning" sx={{ mb: 2 }}>
						<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
							Are you sure you want to delete "{alertData?.name}"?
						</Typography>
						<Typography variant="body2" sx={{ mt: 0.5 }}>
							This action cannot be undone.
						</Typography>
					</Alert>

					{isReceived && (
						<Alert severity="info" sx={{ mb: 2 }}>
							<Typography variant="body2">
								This is a shared alert. Deleting will only remove it from your account. 
								The original alert will remain intact.
							</Typography>
						</Alert>
					)}

					{isShared && !isReceived && (
						<Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
							<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#333' }}>
								Delete Options
							</Typography>
							<Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
								This alert has been shared with others. Choose how you want to delete it:
							</Typography>
							
							<RadioGroup
								value={deleteOption}
								onChange={(e) => setDeleteOption(e.target.value)}
								sx={{ gap: 1 }}
							>
								<FormControlLabel
									value="self"
									control={<Radio size="small" />}
									label={
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<PersonIcon sx={{ fontSize: 16, color: '#666' }} />
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
													Delete for myself only
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Others who have this alert can still use it
												</Typography>
											</Box>
										</Box>
									}
									sx={{ 
										alignItems: 'flex-start',
										'& .MuiFormControlLabel-label': { mt: 0.5 }
									}}
								/>
								
								<FormControlLabel
									value="all"
									control={<Radio size="small" />}
									label={
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<GroupIcon sx={{ fontSize: 16, color: '#666' }} />
											<Box>
												<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
													Delete for everyone
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Remove this alert from all users you've shared with
												</Typography>
											</Box>
										</Box>
									}
									sx={{ 
										alignItems: 'flex-start',
										'& .MuiFormControlLabel-label': { mt: 0.5 }
									}}
								/>
							</RadioGroup>
						</Box>
					)}						
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
				<Button 
					onClick={handleClose}
					variant="outlined"
					disabled={isLoading}
				>
					Cancel
				</Button>
				<Button
					onClick={handleConfirm}
					variant="contained"
					color="error"
					disabled={isLoading}
					startIcon={isLoading ? undefined : <DeleteForeverIcon />}
				>
					{isLoading ? 'Deleting...' : 'Delete Alert'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AlertDeleteConfirmationDialog; 